import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import { useEffect, useRef, lazy, Suspense, type ComponentType } from 'react'
import { APP_ROUTES } from '@/common/constants'
import { APP } from '@/common/strings'
import { analytics } from '@/common/analytics/analytics'
import { ONBOARDING_COPY } from '@/locales/en'
import { AboutYouForm } from './features/about-you/components/AboutYouForm'
import { useAboutYouLogic } from './features/about-you/useAboutYouLogic'
import useUserStore from '@/user/userStore'
import { useOnboardingProgress } from './useOnboardingProgress'
import { useLocalFlags } from '@/common/featureFlags/local/useLocalFlags'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import type { PageProps as AboutYouV2Props } from './features/about-you-v2/components/AboutYouV2'

let LazyAboutYouV2:
    | ComponentType<AboutYouV2Props & { onSubmit?: (data: any) => void; onContactSupportClick?: () => void }>
    | undefined

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'dev') {
    LazyAboutYouV2 = lazy(() =>
        import('./features/about-you-v2/components/AboutYouV2').then((m) => ({ default: m.AboutYouV2 })),
    )
}

/** Production page — wires useAboutYouLogic result to AboutYouForm.
 *  Navigation guards are kept here (not in the hook) since they are route concerns, not business logic.
 */
export function AboutYou() {
    const navigate = useNavigate()
    const { progress } = useOnboardingProgress()
    const logic = useAboutYouLogic()
    const { user } = useUserStore()

    // Capture the original mobile number before form submission
    const originalMobileRef = useRef(user?.mobile)

    // Guard 1: On successful submit → track analytics + navigate
    useEffect(() => {
        if (!logic.isSuccess) return
        analytics.trackWithDefaults('onboarding_user_submitted')
        analytics.identify(user?.userId, {
            firstName: logic.data?.firstName,
            lastName: logic.data?.lastName,
            salutation: logic.data?.salutation,
            middleName: logic.data?.middleName,
        })

        // Normalize for comparison
        const normalize = (num: string) => num.replace(/\D/g, '')
        const currentMobile = normalize(`${logic.data?.countryCode || ''}${logic.data?.mobile || ''}`)
        const originalMobile = normalize(originalMobileRef.current || '')

        if (currentMobile && originalMobile === currentMobile) {
            navigate(APP_ROUTES.SURVEY)
        } else {
            navigate(APP_ROUTES.MOBILE_VERIFICATION)
        }
    }, [logic.isSuccess, logic.data, navigate, user?.userId])

    const { showOnboardingV2 } = useLocalFlags()

    const helmet = (
        <Helmet>
            <title>
                {ONBOARDING_COPY.aboutYou.pageTitle} | {APP.title}
            </title>
        </Helmet>
    )

    if (showOnboardingV2 && LazyAboutYouV2) {
        const AboutYouV2 = LazyAboutYouV2
        return (
            <>
                {helmet}
                <Suspense fallback={<FullScreenLoader dark />}>
                    <AboutYouV2
                        defaultValues={logic.data ? { ...logic.data, newsletterSubscribed: false } : undefined}
                        saving={logic.saving}
                        onSubmit={logic.onSubmit}
                    />
                </Suspense>
            </>
        )
    }

    return (
        <>
            {helmet}
            <AboutYouForm
                progress={progress}
                defaultValues={logic.data}
                loading={logic.loading}
                saving={logic.saving}
                onSubmit={logic.onSubmit}
            />
        </>
    )
}
